import React, { useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { UIProvider, useUIContext } from './context/UIContext.jsx';
import { MovieProvider, useMovieContext } from './context/MovieContext.jsx';
import { LibraryProvider, useLibraryContext } from './context/LibraryContext.jsx';

import Header from './components/Header.jsx';
import Snackbar from './components/Snackbar.jsx';
import HomePage from './pages/HomePage.jsx';
import MovieDetailPage from './pages/MovieDetailPage.jsx';
import ActorDetailPage from './pages/ActorDetailPage.jsx';
import LibraryPage from './pages/LibraryPage.jsx';
import AuthPage from './pages/AuthPage.jsx';

import CategoryModal from './components/CategoryModal.jsx';
import AddToCategoryModal from './components/AddToCategoryModal.jsx';
import RecommendationsModal from './components/RecommendationsModal.jsx';

const App = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  if (!currentUser) {
    return <AuthPage />;
  }

  return (
    <UIProvider>
      <AppContent logout={logout} navigate={navigate} />
    </UIProvider>
  );
};

const AppContent = ({ logout, navigate }) => {
  const { snackbar, showSnackbar } = useUIContext();

  return (
    <MovieProvider showSnackbar={showSnackbar}>
      <LibraryProvider showSnackbar={showSnackbar}>
        <AppRoutes logout={logout} navigate={navigate} />
        <AppModals />
        <Snackbar
          message={snackbar.message}
          visible={snackbar.visible}
          type={snackbar.type}
        />
      </LibraryProvider>
    </MovieProvider>
  );
};

const AppRoutes = ({ logout, navigate }) => {
  const { setSelectedMovie, setSelectedActor, fetchActorDetails } = useMovieContext();

  const handleMovieClick = useCallback(
    (movie) => {
      setSelectedMovie(movie);
      navigate(`/movie/${movie.id}`);
    },
    [setSelectedMovie, navigate]
  );

  const handleActorClick = useCallback(
    (actorId) => {
      fetchActorDetails(actorId);
      navigate(`/actor/${actorId}`);
    },
    [fetchActorDetails, navigate]
  );

  const handleNavigateToLibrary = useCallback(() => {
    navigate('/library');
  }, [navigate]);

  const handleBackToHome = useCallback(() => {
    setSelectedMovie(null);
    setSelectedActor(null);
    navigate('/');
  }, [setSelectedMovie, setSelectedActor, navigate]);

  return (
    <div className="min-h-screen bg-gray-950 font-inter text-gray-100 p-4 sm:p-8">
      <Header onNavigateToLibrary={handleNavigateToLibrary} onLogout={logout} />
      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              handleMovieClick={handleMovieClick}
            />
          }
        />
        <Route
          path="/movie/:id"
          element={
            <MovieDetailPage
              handleBackToHome={handleBackToHome}
              handleMovieClick={handleMovieClick}
              handleActorClick={handleActorClick}
            />
          }
        />
        <Route
          path="/actor/:id"
          element={
            <ActorDetailPage
              handleBackToHome={handleBackToHome}
              handleMovieClick={handleMovieClick}
            />
          }
        />
        <Route
          path="/library"
          element={
            <LibraryPage
              handleMovieClick={handleMovieClick}
              handleBackToHome={handleBackToHome}
            />
          }
        />
      </Routes>
    </div>
  );
};

const AppModals = () => {
  const {
    showAddCategoryModal,
    closeAddCategoryModal,
    showAddToCategoryModal,
    closeAddToCategoryModal,
    newCategoryName,
    setNewCategoryName,
    createCategory,
    categoryErrorMessage,
    selectedMovieForCategory,
    categories,
    toggleMovieCategory,
    showRecommendationsModal,
    closeRecommendationsModal,
    isGeneratingRecommendations,
    recommendationsList,
    recommendationError,
  } = useLibraryContext();

  return (
    <>
      <CategoryModal
        show={showAddCategoryModal}
        onClose={closeAddCategoryModal}
        categoryName={newCategoryName}
        onCategoryNameChange={(e) => setNewCategoryName(e.target.value)}
        onCreateCategory={createCategory}
        errorMessage={categoryErrorMessage}
      />

      <AddToCategoryModal
        show={showAddToCategoryModal}
        onClose={closeAddToCategoryModal}
        movie={selectedMovieForCategory}
        categories={categories}
        onToggleCategory={toggleMovieCategory}
        newCategoryName={newCategoryName}
        onCategoryNameChange={(e) => setNewCategoryName(e.target.value)}
        onCreateCategory={createCategory}
        errorMessage={categoryErrorMessage}
      />

      <RecommendationsModal
        show={showRecommendationsModal}
        onClose={closeRecommendationsModal}
        isGenerating={isGeneratingRecommendations}
        recommendations={recommendationsList}
        error={recommendationError}
      />
    </>
  );
};

const AppWrapper = () => {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
};

export default AppWrapper;
